import {
  GET_FORMATTED_CITATIONSV2,
  GET_STYLES_V2,
} from '@chegg-wt/core/oneGraphQueries/FormattedCitations'
import {
  GET_PAPER,
  GET_PAPER_LIST,
  PAPER_CHECK,
  SENTENCE_CHECK,
} from '@chegg-wt/core/oneGraphQueries/Paraphraser'
import { getActualStyleId, popularStyles } from '@chegg-wt/core/utils/styles'
import {
  ExpressKeys,
  ExpressKeys_expressKeys,
} from '@chegg-wt/core/oneGraphQueries/__generated__/ExpressKeys'
import { EXPRESS_KEYS } from '@chegg-wt/core/oneGraphQueries/ExpressKeys'
import {
  SentenceCheck,
  SentenceCheckVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/SentenceCheck'
import {
  GetPaper,
  GetPaperVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/GetPaper'
import {
  GetPaperList,
  GetPaperListVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/GetPaperList'
import {
  GetFormattedCitationsV2,
  GetFormattedCitationsV2Variables,
} from '../oneGraphQueries/__generated__/GetFormattedCitationsV2'
import {
  GetCitationStylesV2,
  GetCitationStylesV2Variables,
} from '../oneGraphQueries/__generated__/GetCitationStylesV2'
import {
  PaperCheck,
  PaperCheck_paperCheck,
  PaperCheckVariables,
} from '../oneGraphQueries/__generated__/PaperCheck'
import { client } from './graphApi'

export const oneGraph = {
  getCitationsV2: (variables: GetFormattedCitationsV2Variables) => {
    return client.query<GetFormattedCitationsV2>({
      query: GET_FORMATTED_CITATIONSV2,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })
  },
  getStylesV2: ({ keyword, page = 1 }: GetCitationStylesV2Variables) => {
    return client.query<GetCitationStylesV2>({
      query: GET_STYLES_V2,
      variables: {
        keyword,
        page,
      },
      context: {
        clientName: 'oneGraph',
      },
    })
  },
  getExpressKeys: async (): Promise<ExpressKeys_expressKeys> => {
    try {
      const { data, errors } = await client.query<ExpressKeys>({
        query: EXPRESS_KEYS,
        context: {
          clientName: 'oneGraph',
        },
      })

      if (errors && errors.length > 0) {
        throw new Error('Could not getKeys while creating new channel')
      }
      return data.expressKeys
    } catch {
      throw new Error('Could not getKeys while creating new channel')
    }
  },

  paperCheck: async (variables: PaperCheckVariables): Promise<PaperCheck_paperCheck> => {
    const { data, errors } = await client.mutate<PaperCheck>({
      mutation: PAPER_CHECK,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors && errors.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in paperCheck mutation: ${errorMessages}`)
    }

    return data.paperCheck
  },
  sentenceCheck: async (variables: SentenceCheckVariables) => {
    const { data, errors } = await client.mutate<SentenceCheck>({
      mutation: SENTENCE_CHECK,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors && errors.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in sentenceCheck mutation: ${errorMessages}`)
    }
    return data.sentenceCheck
  },
  getPaper: async (variables: GetPaperVariables) => {
    const { data, errors } = await client.query<GetPaper>({
      query: GET_PAPER,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors?.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in getPaper query: ${errorMessages}`)
    }
    return data.getPaper
  },
  getPaperList: async (variables: GetPaperListVariables) => {
    const { data, errors } = await client.query<GetPaperList>({
      query: GET_PAPER_LIST,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors?.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in getPaperList query: ${errorMessages}`)
    }
    return data.getPaperList
  },
}

export const getUrlStyleNameV2 = async (style: string, page = 1) => {
  const actualStyleId = getActualStyleId(style)
  const pop = popularStyles[actualStyleId]
  if (pop) {
    return pop
  }
  try {
    const stylesV2Response = await oneGraph.getStylesV2({ keyword: actualStyleId, page })
    const { name } = stylesV2Response.data.citationStylesV2.results[0]
    return [name, name]
  } catch {
    const name = style.toUpperCase()
    return [name, name]
  }
}
